import { Logger } from '@frontend/logger';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

const SENSOR_TIMEOUT = 10000;

interface ViewProps {
    isAuthenticated: boolean;
    isProcessing: boolean;
    isIdentifying: boolean;
    error: ReactNode | null;
    resetError: () => void;
}

export const useSetCurrentUserAsSender = (): ViewProps => {
    const intl = useIntl();
    const { store, navigationState, onSubmit, updateWizardData } = useScreen();
    const [error, setError] = useState<ReactNode | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isIdentifying, setIsIdentifying] = useState(false);
    const hasCompletedRef = useRef(false);
    const isProcessingRef = useRef(false);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { syncTransaction } = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoCreate: false,
        autoFetch: true
    });

    const clearSensorTimeout = useCallback(() => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    }, []);

    const identificationErrorMessage = intl.formatMessage({
        id: 'kiosk.wizard.set-current-user-as-sender.identification-error',
        description: 'Error shown when no matching identification is found on the set current user as sender screen',
        defaultMessage: 'No matching identification was found. Please try again.'
    });

    const setSenderAndContinue = useCallback(
        async (userId: string) => {
            if (hasCompletedRef.current || isProcessingRef.current || !store) return;

            isProcessingRef.current = true;
            setIsProcessing(true);
            setError(null);

            try {
                const syncedTransaction = await syncTransaction({ user_id: userId });
                const transactionId = syncedTransaction.id;
                const accountId = store.wizard.account_id;

                const existingSenders = await TransactionUserClient.fetchTransactionUsers({
                    transaction_id: transactionId,
                    type: TransactionUserType.SENDER
                });

                const senderExists = existingSenders.results.some((sender) => sender.user_id === userId);
                let sender: TransactionUser;

                if (!senderExists) {
                    sender = await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                        user_id: userId,
                        type: TransactionUserType.SENDER
                    });
                } else {
                    sender = existingSenders.results.find((existingSender) => existingSender.user_id === userId)!;
                }

                updateWizardData({ senders: [sender], transaction: { ...syncedTransaction, user_id: userId } });
                hasCompletedRef.current = true;
                onSubmit();
            } catch (e) {
                Logger.error('Failed to set current user as sender', e);
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.set-current-user-as-sender.failed',
                        description: 'Error shown when setting the identified user as transaction sender fails',
                        defaultMessage: 'Something went wrong. Please try again.'
                    })
                );
                isProcessingRef.current = false;
            } finally {
                setIsProcessing(false);
            }
        },
        [intl, onSubmit, store, syncTransaction, updateWizardData]
    );

    useEffect(() => {
        const userId = navigationState.user?.id;
        if (!userId || hasCompletedRef.current) return;
        setSenderAndContinue(userId);
    }, [navigationState.user?.id, setSenderAndContinue]);

    const handleUserIdentified = useCallback(
        (user: IUser) => {
            if (navigationState.user || hasCompletedRef.current) return;
            clearSensorTimeout();
            setIsIdentifying(false);
            setSenderAndContinue(user.id);
        },
        [clearSensorTimeout, navigationState.user, setSenderAndContinue]
    );

    useScanners({
        id: 'SET_CURRENT_USER_AS_SENDER',
        onDeviceSensorReceived: () => {
            if (navigationState.user) return;
            setIsIdentifying(true);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                setIsIdentifying(false);
                setError(identificationErrorMessage);
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });

    useIdentification({
        id: 'SET_CURRENT_USER_AS_SENDER',
        onNoMatchingIdentificationFound: () => {
            clearSensorTimeout();
            setIsIdentifying(false);
            setError(identificationErrorMessage);
        },
        onUnlinkedIdentificationFound: () => {
            clearSensorTimeout();
            setIsIdentifying(false);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.set-current-user-as-sender.unlinked-identification',
                    description: 'Error shown when a scanned badge is recognized but not linked to a user',
                    defaultMessage: 'This badge was recognized but is not linked to a user yet. Please try a different badge or contact support.'
                })
            );
        },
        onUserIdentified: handleUserIdentified
    });

    useEffect(() => {
        return () => {
            clearSensorTimeout();
        };
    }, [clearSensorTimeout]);

    return {
        isAuthenticated: !!navigationState.user,
        isProcessing,
        isIdentifying,
        error,
        resetError: useCallback(() => setError(null), [])
    };
};
